/**
 * 検索パネル「都道府県での絞り込み」の「よく使う」グループ（2026-10-15 ユーザー指定）。
 * 選んだ都道府県の回数をこのブラウザ（localStorage）に覚えておき、回数の多い順に FAVORITE_AREAS_MAX 件を一覧の先頭に出す。
 * 回数は都道府県の名前（「13.東京」など）で数えるので、ヤマダ・エディオン・ドンキで共通
 */
export const AREA_USAGE_KEY = 'rakujanAreaUsage';
export const FAVORITE_AREAS_MAX = 5;
/** 「よく使う」グループの value（本来の都道府県グループと見分けるため） */
export const FAVORITE_GROUP_VALUE = '__favorite_areas__';
export const FAVORITE_GROUP_LABEL = 'よく使う';

export type AreaOption = { label: string; value: string };
export type AreaGroup = { label: string; value: string; items: AreaOption[] };

/** 都道府県名 → 選んだ回数。読めない時は空 */
export function loadAreaUsage(): Record<string, number> {
  try {
    const raw = localStorage.getItem(AREA_USAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** 選んだ都道府県の回数を 1 ずつ増やして覚える。保存できなくても落とさない */
export function noteAreaUsage(labels: readonly string[]): Record<string, number> {
  const usage = loadAreaUsage();
  for (const label of labels) {
    if (label) usage[label] = (usage[label] ?? 0) + 1;
  }
  try {
    localStorage.setItem(AREA_USAGE_KEY, JSON.stringify(usage));
  } catch {
    // プライベートモードなどで保存できない時は、この画面の間だけ効く
  }
  return usage;
}

/** 回数の多い順に FAVORITE_AREAS_MAX 件（同じ回数なら一覧の順）。一度も選んでいないものは入れない */
export function favoriteAreas(items: readonly AreaOption[], usage: Record<string, number>): AreaOption[] {
  return items
    .map((item, index) => ({ item, index, count: usage[item.label] ?? 0 }))
    .filter((x) => x.count > 0)
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .slice(0, FAVORITE_AREAS_MAX)
    .map((x) => x.item);
}

/**
 * 都道府県グループの先頭に「よく使う」グループを付ける（よく使うものが無ければ付けない）。
 * すでに付いていれば付け直す（選ぶたびに順番が変わるため）
 */
export function withFavoriteAreas(groups: readonly AreaGroup[], usage: Record<string, number>): AreaGroup[] {
  const base = groups.filter((g) => g.value !== FAVORITE_GROUP_VALUE);
  const top = favoriteAreas(
    base.flatMap((g) => g.items),
    usage,
  );
  if (top.length === 0) return base;
  return [{ label: FAVORITE_GROUP_LABEL, value: FAVORITE_GROUP_VALUE, items: top }, ...base];
}

/** 今選んでいる都道府県の名前（value → label） */
export function selectedAreaLabels(groups: readonly AreaGroup[], selected: readonly string[]): string[] {
  const base = groups.filter((g) => g.value !== FAVORITE_GROUP_VALUE);
  return base
    .flatMap((g) => g.items)
    .filter((i) => selected.includes(i.value))
    .map((i) => i.label);
}
